/**
 * Version Details Slideout
 * Displays comprehensive version information in a slide-out panel
 */

import React from 'react';
import {
  XMarkIcon as X,
  ArrowDownTrayIcon as Download,
  CalendarIcon as Calendar,
  ShieldCheckIcon as Shield,
  ExclamationTriangleIcon as AlertTriangle,
  CheckCircleIcon as CheckCircle,
  ArrowTopRightOnSquareIcon as ExternalLink,
} from '@heroicons/react/24/outline';

interface VersionDetailsSlideoutProps {
  version: any;
  isOpen: boolean;
  onClose: () => void;
  onRerunAI?: (versionId: number) => void;
  isAdmin?: boolean;
}

export const VersionDetailsSlideout: React.FC<VersionDetailsSlideoutProps> = ({
  version,
  isOpen,
  onClose,
  onRerunAI,
  isAdmin = false,
}) => {
  if (!isOpen || !version) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Slideout Panel */}
      <div className="fixed right-0 top-0 h-full w-full md:w-2/3 lg:w-1/2 bg-[#0a0e1a] border-l border-cyber-blue-500/30 z-50 overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-dark-bg-primary border-b border-cyber-blue-500/30 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">
              Version {version.version_number}
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              {version.os_platform} • {version.architecture}
              {version.release_channel && (
                <span className="ml-2 text-cyber-blue-400">• {version.release_channel}</span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-dark-bg-secondary rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status Badges */}
          <div className="flex flex-wrap gap-2">
            {version.is_latest && (
              <span className="px-3 py-1 bg-green-900/30 border border-green-700/50 text-green-400 rounded-full text-sm">
                <CheckCircle className="w-4 h-4 inline mr-1" />
                Latest Version
              </span>
            )}
            {version.is_security_update && (
              <span className="px-3 py-1 bg-red-900/30 border border-red-700/50 text-red-400 rounded-full text-sm">
                <Shield className="w-4 h-4 inline mr-1" />
                Security Update
              </span>
            )}
            {version.cve_count > 0 && (
              <span className="px-3 py-1 bg-yellow-900/30 border border-yellow-700/50 text-yellow-400 rounded-full text-sm">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                {version.cve_count} CVE{version.cve_count !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Release Info */}
          <div className="bg-dark-bg-secondary rounded-lg p-4 space-y-3">
            <h3 className="text-lg font-semibold text-white mb-3">Release Information</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-gray-400 text-sm">Release Date</p>
                <p className="text-white flex items-center gap-2 mt-1">
                  <Calendar className="w-4 h-4 text-cyber-blue-400" />
                  {version.release_date
                    ? new Date(version.release_date).toLocaleDateString()
                    : 'Unknown'}
                </p>
              </div>

              {version.file_size_bytes && (
                <div>
                  <p className="text-gray-400 text-sm">File Size</p>
                  <p className="text-white mt-1">
                    {(version.file_size_bytes / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              )}
            </div>

            {version.cpe_string && (
              <div className="mt-3">
                <p className="text-gray-400 text-sm">CPE String</p>
                <code className="text-xs text-cyber-blue-400 bg-dark-bg-primary px-2 py-1 rounded mt-1 block break-all">
                  {version.cpe_string}
                </code>
              </div>
            )}
          </div>

          {/* AI Enrichment */}
          <div className="bg-dark-bg-secondary rounded-lg p-4 space-y-3 border border-cyber-blue-500/20">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                AI Analysis
              </h3>
              {isAdmin && version.platforms && version.platforms.length > 0 && onRerunAI && (
                <button
                  onClick={() => onRerunAI(version.platforms[0].id)}
                  className="px-3 py-1 text-xs bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded hover:bg-purple-500/20 transition-colors flex items-center gap-1"
                  title="Re-run AI Analysis"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Re-run AI
                </button>
              )}
            </div>

            {version.enrichment ? (
              <>

              {version.enrichment.deployment_priority_score && (
                <div>
                  <p className="text-gray-400 text-sm">Deployment Priority</p>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex-1 bg-dark-bg-primary rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-green-500 to-red-500 h-2 rounded-full"
                        style={{ width: `${(version.enrichment.deployment_priority_score / 10) * 100}%` }}
                      />
                    </div>
                    <span className="text-white font-semibold">
                      {version.enrichment.deployment_priority_score.toFixed(1)}/10
                    </span>
                  </div>
                </div>
              )}

              {version.enrichment.importance_level && (
                <div>
                  <p className="text-gray-400 text-sm">Importance Level</p>
                  <span
                    className={`inline-block px-3 py-1 rounded mt-1 text-sm ${
                      version.enrichment.importance_level === 'critical'
                        ? 'bg-red-900/30 border border-red-700/50 text-red-400'
                        : version.enrichment.importance_level === 'high'
                        ? 'bg-orange-900/30 border border-orange-700/50 text-orange-400'
                        : version.enrichment.importance_level === 'medium'
                        ? 'bg-yellow-900/30 border border-yellow-700/50 text-yellow-400'
                        : 'bg-gray-900/30 border border-gray-700/50 text-gray-400'
                    }`}
                  >
                    {version.enrichment.importance_level.toUpperCase()}
                  </span>
                </div>
              )}

              {version.enrichment.release_summary && (
                <div>
                  <p className="text-gray-400 text-sm mb-2">Release Summary</p>
                  <p className="text-white text-sm leading-relaxed">
                    {version.enrichment.release_summary}
                  </p>
                </div>
              )}

              {version.enrichment.recommended_action && (
                <div>
                  <p className="text-gray-400 text-sm mb-2">Recommended Action</p>
                  <p className="text-white text-sm">
                    {version.enrichment.recommended_action}
                  </p>
                </div>
              )}

              {version.enrichment.deployment_complexity && (
                <div>
                  <p className="text-gray-400 text-sm">Deployment Complexity</p>
                  <span className="text-white mt-1 block capitalize">
                    {version.enrichment.deployment_complexity}
                  </span>
                </div>
              )}

              {version.enrichment.patch_details && (
                <div>
                  <p className="text-gray-400 text-sm mb-2">Patch Details</p>
                  <p className="text-white text-sm leading-relaxed">
                    {version.enrichment.patch_details}
                  </p>
                </div>
              )}

              {version.enrichment.business_impact_analysis && (
                <div>
                  <p className="text-gray-400 text-sm mb-2">Business Impact</p>
                  <p className="text-white text-sm leading-relaxed">
                    {version.enrichment.business_impact_analysis}
                  </p>
                </div>
              )}

              {version.enrichment.deployment_notes && (
                <div>
                  <p className="text-gray-400 text-sm mb-2">Deployment Notes</p>
                  <p className="text-white text-sm leading-relaxed">
                    {version.enrichment.deployment_notes}
                  </p>
                </div>
              )}
              </>
            ) : (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <p className="text-gray-400 text-sm">No AI analysis available yet</p>
                {isAdmin && version.platforms && version.platforms.length > 0 && onRerunAI && (
                  <button
                    onClick={() => onRerunAI(version.platforms[0].id)}
                    className="mt-3 px-4 py-2 text-sm bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded hover:bg-purple-500/20 transition-colors"
                  >
                    Run AI Analysis
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Security Fixes */}
          {version.enrichment?.security_fixes && version.enrichment.security_fixes.length > 0 && (
            <div className="bg-dark-bg-secondary rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-400" />
                Security Fixes
              </h3>
              <ul className="space-y-2">
                {version.enrichment.security_fixes.map((fix: string, idx: number) => (
                  <li key={idx} className="text-white text-sm flex items-start gap-2">
                    <span className="text-red-400 mt-1">•</span>
                    <span>{fix}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Platforms Table */}
          {version.platforms && version.platforms.length > 0 && (
            <div className="bg-dark-bg-secondary rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-3">Available Platforms</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">Platform</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">Architecture</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">File Size</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {version.platforms.map((platform: any, idx: number) => (
                      <tr key={idx} className="border-b border-gray-800">
                        <td className="py-2 px-3 text-white capitalize">{platform.os_platform}</td>
                        <td className="py-2 px-3 text-gray-300">{platform.architecture}</td>
                        <td className="py-2 px-3 text-gray-300">
                          {platform.file_size_bytes
                            ? `${(platform.file_size_bytes / 1024 / 1024).toFixed(2)} MB`
                            : '-'}
                        </td>
                        <td className="py-2 px-3">
                          {platform.download_url && (
                            <a
                              href={platform.download_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-cyber-blue-400 hover:text-cyber-blue-300 text-xs"
                            >
                              <Download className="w-4 h-4 inline" />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CVE Information */}
          {version.cves && version.cves.length > 0 && (
            <div className="bg-dark-bg-secondary rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-400" />
                Known Vulnerabilities ({version.cves.length})
              </h3>
              {(() => {
                const affectedCVEs = version.cves.filter((cve: any) => cve.status === 'affected');
                const fixedCVEs = version.cves.filter((cve: any) => cve.status === 'fixed');
                const unknownCVEs = version.cves.filter((cve: any) => !cve.status || (cve.status !== 'affected' && cve.status !== 'fixed'));
                const sortedCVEs = [...affectedCVEs, ...unknownCVEs, ...fixedCVEs];

                return (
                  <>
                    {affectedCVEs.length > 0 && (
                      <div className="mb-3 text-sm text-red-400 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        {affectedCVEs.length} Vulnerable • {fixedCVEs.length} Patched
                      </div>
                    )}
                    <div className="space-y-3">
                      {sortedCVEs.map((cve: any, idx: number) => (
                        <div
                          key={idx}
                          className={`bg-dark-bg-tertiary rounded-lg p-3 border transition-colors ${
                            cve.status === 'affected'
                              ? 'border-red-700/50 hover:border-red-500/70'
                              : cve.status === 'fixed'
                              ? 'border-green-700/30 hover:border-green-500/50'
                              : 'border-gray-700 hover:border-cyber-blue-500/50'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <a
                              href={`/vulnerability/${cve.cve_id}`}
                              className="text-cyber-blue-400 hover:text-cyber-blue-300 font-semibold font-mono"
                              onClick={(e) => {
                                e.preventDefault();
                                window.location.href = `/vulnerability/${cve.cve_id}`;
                              }}
                            >
                              {cve.cve_id}
                            </a>
                            {cve.severity && (
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                  cve.severity.toLowerCase() === 'critical'
                                    ? 'bg-red-900/30 border border-red-700/50 text-red-400'
                                    : cve.severity.toLowerCase() === 'high'
                                    ? 'bg-orange-900/30 border border-orange-700/50 text-orange-400'
                                    : cve.severity.toLowerCase() === 'medium'
                                    ? 'bg-yellow-900/30 border border-yellow-700/50 text-yellow-400'
                                    : 'bg-gray-900/30 border border-gray-700/50 text-gray-400'
                                }`}
                              >
                                {cve.severity}
                              </span>
                            )}
                          </div>
                          {cve.description && (
                            <p className="text-gray-300 text-sm line-clamp-2">{cve.description}</p>
                          )}
                          {cve.status && (
                            <div className="mt-2 flex items-center gap-2">
                              {cve.status === 'fixed' ? (
                                <span className="px-2 py-0.5 bg-green-900/30 border border-green-700/50 text-green-400 rounded text-xs flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  Patched in this version
                                </span>
                              ) : cve.status === 'affected' ? (
                                <span className="px-2 py-0.5 bg-red-900/30 border border-red-700/50 text-red-400 rounded text-xs flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" />
                                  Vulnerable
                                </span>
                              ) : null}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* Release Notes */}
          {version.release_notes_url && (
            <div className="bg-dark-bg-secondary rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-3">Release Notes</h3>
              <a
                href={version.release_notes_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-cyber-blue-400 hover:text-cyber-blue-300 transition-colors"
              >
                View Release Notes
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
